// The sound-word toast's queue (WI-06, L3-02): each word holds for at least HOLD_MS and the next
// one waits its turn instead of overwriting it. A word from a rare or legendary act holds until
// the act ends (unless another word is waiting). Pure, so the timing is unit-tested; the store in
// SfxToast drives it with one timer. Times are performance.now() ms, never the (fakeable) clock.

/** A word shown as it is (a place's beat), rather than looked up in the pack's sfx deck. */
export interface InlineSfx {
  script: string
  reading: string
  meaning: string
  rare?: boolean
}

export type SfxRef = string | InlineSfx

export interface SfxItem {
  id: number
  card: SfxRef
  /** The act's point on screen when it comes from the world (the word keeps clear of it); null: a tap. */
  at: { x: number; y: number } | null
  /** From a rare or legendary act: stays up until that act ends. */
  untilActEnds: boolean
}

export interface SfxQueue {
  shown: SfxItem | null
  /** When `shown` went up (or was last refreshed). */
  since: number
  waiting: SfxItem[]
  /** The mini card is open: nothing moves on until it closes. */
  open: boolean
}

/** Every word stays at least this long (the review measured 1.1–1.5 s). */
export const HOLD_MS = 3000
/** Words waiting beyond this many drop the oldest, so a burst never backs up for long. */
export const MAX_WAITING = 3

export const emptyQueue = (): SfxQueue => ({ shown: null, since: 0, waiting: [], open: false })

const key = (c: SfxRef) => (typeof c === 'string' ? c : c.script)

/** A new word: shown now if nothing is, else queued. The same word again just restarts its hold. */
export function push(q: SfxQueue, item: SfxItem, now: number): SfxQueue {
  if (!q.shown) return { ...q, shown: item, since: now, open: false }
  if (key(q.shown.card) === key(item.card) && q.waiting.length === 0) {
    return { ...q, shown: { ...item, id: q.shown.id }, since: now }
  }
  const last = q.waiting[q.waiting.length - 1]
  if (last && key(last.card) === key(item.card)) return q
  const waiting = [...q.waiting, item]
  return { ...q, waiting: waiting.slice(Math.max(0, waiting.length - MAX_WAITING)) }
}

/** Whether `shown` may give way now. */
function due(q: SfxQueue, now: number, actRunning: boolean): boolean {
  if (!q.shown || q.open) return false
  if (now - q.since < HOLD_MS) return false
  return !(q.shown.untilActEnds && actRunning && q.waiting.length === 0)
}

/** Moves the queue on if the word showing has had its time. */
export function tick(q: SfxQueue, now: number, actRunning: boolean): SfxQueue {
  if (!due(q, now, actRunning)) return q
  const [next, ...rest] = q.waiting
  return { shown: next ?? null, since: now, waiting: rest, open: false }
}

/** Milliseconds until `tick` could change something (null: nothing to wait for on a timer). */
export function wait(q: SfxQueue, now: number, actRunning: boolean): number | null {
  if (!q.shown || q.open) return null
  const left = HOLD_MS - (now - q.since)
  if (left > 0) return left
  // Held for its act: the act ending (or another word) wakes it, not the timer.
  return due(q, now, actRunning) ? 0 : null
}

/** Opens or closes the mini card. Closing lets the word go (it has been read). */
export function setOpen(q: SfxQueue, open: boolean, now: number): SfxQueue {
  if (!q.shown) return q
  if (open) return { ...q, open: true }
  const [next, ...rest] = q.waiting
  return { shown: next ?? null, since: now, waiting: rest, open: false }
}

// ---------- where the word shows ----------

/** Space kept between the word and the HUD bands, the ticker and the screen edges. */
export const TOAST_GAP = 8
/** Room kept for the mini card under the pill, so opening it never covers the act either. */
export const CARD_ROOM = 130
/** The act's point must sit at least this far (px) outside the word's zone. */
export const ACT_CLEAR = 24

export type ToastBand = 'top' | 'bottom'

/**
 * The band the word shows in (owner, 23 Oct: "the acts have their accompanying text appearing
 * directly over the actual act"; WI-06 had put it at the actor). Never at the act: a fixed band,
 * centred, just under the top HUD, unless the act's point (`at`, on screen) is in that band's zone
 * (the pill plus room for its mini card); then the band just above the ticker and stop bar. A word
 * from a tap with no point goes in the top band. When neither band is clear, the one whose zone
 * is farther from the point.
 */
export function toastBand(pillH: number, at: { y: number } | null, strip: { top: number; bottom: number }): ToastBand {
  if (!at) return 'top'
  const h = pillH + CARD_ROOM
  const top = { lo: strip.top + TOAST_GAP, hi: strip.top + TOAST_GAP + h }
  const bottom = { lo: strip.bottom - TOAST_GAP - h, hi: strip.bottom - TOAST_GAP }
  const clear = (z: { lo: number; hi: number }) => at.y < z.lo - ACT_CLEAR || at.y > z.hi + ACT_CLEAR
  if (clear(top)) return 'top'
  if (clear(bottom)) return 'bottom'
  const far = (z: { lo: number; hi: number }) => Math.min(Math.abs(at.y - z.lo), Math.abs(at.y - z.hi))
  return far(bottom) > far(top) ? 'bottom' : 'top'
}

/**
 * The word's top-left (px in its container) in its band: centred across, its top just under the
 * top HUD (top band) or its bottom just above the ticker / stop bar (bottom band: it grows
 * upwards when the mini card opens). A strip too short for it keeps its top under the top HUD.
 */
export function placeToast(size: { w: number; h: number }, band: ToastBand, strip: { width: number; top: number; bottom: number }): { left: number; top: number } {
  const left = Math.max(TOAST_GAP, Math.round((strip.width - size.w) / 2))
  const lo = strip.top + TOAST_GAP
  const hi = strip.bottom - TOAST_GAP - size.h
  return { left, top: band === 'bottom' && hi >= lo ? hi : lo }
}
